<script lang="ts">
	import { combinePageArrays, getPaginatedDocument } from '$lib/helpers/app.documents';
	import { filter, type Content, type Ordering } from '@prismicio/client';
	import type { SliceComponentProps } from '@prismicio/svelte';
	import { inview } from 'svelte-inview';
	import { fade } from 'svelte/transition';
	import type { PlayerDocument } from '../../../prismicio-types';
	import PlayerCard from '$lib/components/PlayerCard.svelte';

	type Props = SliceComponentProps<Content.TeamListSlice>;

	const { slice }: Props = $props();
	const columns = 12 / (slice.primary.columns ?? 3);
	const hdColumns = 12 / ((slice.primary.columns ?? 3) + 1);
	const perPage: number = slice.primary.per_page ?? 1;

	let loading: boolean = $state(false);
	let nextPage: boolean = $state(true);
	let players: PlayerDocument[] = $state([]);
	let pageNumber: number = $state(1);
	let currentFilter: string = $state('');
	let captainFilter: boolean = $state(false);
	let assistantFilter: boolean = $state(false);
	let filters: string[] = $state([]);
	let ordering: Ordering[] = [
		{
			field: 'my.player.number',
			direction: 'asc'
		}
	];

	const handleFilter = async (e: MouseEvent) => {
		e.preventDefault();

		captainFilter = false;
		assistantFilter = false;

		const button = e.currentTarget as HTMLButtonElement;
		if (button) {
			const value = button.dataset.value as string;
			if (value !== '') {
				currentFilter = value;
				filters = [filter.at('my.player.position', value)];
			} else {
				currentFilter = '';
				filters = [];
			}

			pageNumber = 1;
			nextPage = true;
			await loadPlayers(false);
		}
	};

	const handleCaptainFilter = async (e: MouseEvent) => {
		e.preventDefault();
		captainFilter = true;
		assistantFilter = false;
		filters = [];

		filters = [filter.at('my.player.captain', true)];

		pageNumber = 1;
		nextPage = true;
		await loadPlayers(false);
	};

	const handleAssistantFilter = async (e: MouseEvent) => {
		e.preventDefault();
		captainFilter = false;
		assistantFilter = true;
		filters = [];

		filters = [filter.at('my.player.assistant_captain', true)];

		pageNumber = 1;
		nextPage = true;
		await loadPlayers(false);
	};

	const loadPlayers = async (useCombine: boolean) => {
		if (loading) return;

		loading = true;
		await getPaginatedDocument('player', perPage, pageNumber, nextPage, filters, ordering)
			.then((response) => {
				console.log(response);
				if (!response) {
					throw new Error('Error retrieving documents');
				}

				const results = response.results as PlayerDocument[];
				if (players && useCombine) {
					players = combinePageArrays(players, results);
				} else {
					players;
					players = results;
				}

				pageNumber = response.pageNum + 1;
				nextPage = response.nextPage;
			})
			.catch((error) => console.error(error))
			.finally(() => (loading = false));
	};
</script>

<section
	class="team-list spacing-top-{slice.primary.spacing_top} spacing-bottom-{slice.primary
		.spacing_bottom}"
	data-slice-type={slice.slice_type}
	data-slice-variation={slice.variation}
>
	{#if slice.primary.show_filters == true}
		<div class="container-lg px-0 p-lg-3">
			<ul class="team-list__filters spacing-bottom-{slice.primary.spacing_top}">
				<li>
					<button
						class="btn btn-secondary"
						class:active={filters.length < 1}
						onclick={handleFilter}
						data-value="">All</button
					>
				</li>
				<li>
					<button
						class="btn btn-secondary"
						class:active={currentFilter == 'Forward'}
						onclick={handleFilter}
						data-value="Forward">Forward</button
					>
				</li>
				<li>
					<button
						class="btn btn-secondary"
						class:active={currentFilter == 'Defence'}
						onclick={handleFilter}
						data-value="Defence">Defence</button
					>
				</li>
				<li>
					<button
						class="btn btn-secondary"
						class:active={currentFilter == 'Netminder'}
						onclick={handleFilter}
						data-value="Netminder">Netminder</button
					>
				</li>
				<li>
					<button
						class="btn btn-secondary"
						class:active={captainFilter == true}
						onclick={handleCaptainFilter}>Captain</button
					>
				</li>
				<li>
					<button
						class="btn btn-secondary"
						class:active={assistantFilter == true}
						onclick={handleAssistantFilter}>Assistants</button
					>
				</li>
			</ul>
		</div>
	{/if}

	<div class="container">
		<div class="row align-items-stretch g-5">
			{#if players.length}
				{#each players as player}
					{#key player.id}
						<div class="col-12 col-lg-{columns} col-hd-{hdColumns} pt-lg-2" transition:fade>
							<PlayerCard {player} />
						</div>
					{/key}
				{/each}
			{/if}
		</div>

		{#if nextPage}
			<div class="d-flex justify-content-center mt-5">
				<button
					class="btn btn-lg btn-text"
					onclick={() => loadPlayers(true)}
					use:inview
					oninview_enter={() => loadPlayers(true)}
				>
					{#if loading}
						<div class="spinner-border text-primary" role="status">
							<span class="visually-hidden">Loading...</span>
						</div>
					{:else}
						<span>Load more</span>
					{/if}
				</button>
			</div>
		{/if}
	</div>
</section>
